import React, { useState } from "react";
import {
    AppBar,
    Toolbar,
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    Button,
    useMediaQuery,
    Box,
    Divider,
    Drawer,
    ListItemIcon,
    alpha,
    Typography,
    Tooltip,
    Link,
} from "@mui/material";
import {
    Menu as MenuIcon,
    AccountCircle as AccountCircleIcon,
    EmailOutlined as EmailOutlinedIcon,
    ArrowDropDown as ArrowDropDownIcon,
    Person as PersonIcon,
    Logout as LogoutIcon,
    Inventory as InventoryIcon,
    Settings,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import SiteSelection from "../../SiteSelection/SiteSelection";
import GlobalSearch from "../../GlobalSearch/GlobalSearch";
import CartIcon from "../../CartIcon/CartIcon";
import { useAuth } from "../../../context/authContext";
import "../../Header/Header.css";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useTranslation } from "react-i18next";
import { LABELS, MENU_ITEMS, ROUTES, UI_TEXT } from "../../../constants";
import { stringAvatar } from "../../../utilities/globalServiceUtils";
import UserName from "../../UserName/UserName";
import ContactCCRForm from "../../ContactCCRForm/ContactCCRForm";
import NotificationBell from "../../Notifications/NotificationBell";
import WelcomeBox from "../../WelcomBox/WelcomeBox";
import { toast } from "../../../context/ToastContext";
import CompanyLogo from "../../CompanyLogo/CompanyLogo";
import { DOCUMENT_EXTERNAL_ENABLED, DOCUMENT_EXTERNAL_URL } from "../../../config";
import { buildExternalLoginUrl } from "../../../utilities/externalDocs";
import { useSite } from "../../../context/siteContext";
import { useOptions } from "../../../context/optionContext";
import { resolveEnabledAdminModules } from "../../../utilities/adminModules";
import { canAccessOrderAnalytics, canAccessUserAnalytics } from "../../../utilities/pageAccess";
import { launchReach360 } from "../../../utilities/reach360Sso";

const Header = ({ chatOpen, setChatOpen, showChatbot } = {}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [profileAnchorEl, setProfileAnchorEl] = useState(null);
    const [activeMobileSubmenu, setActiveMobileSubmenu] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const isMobile = useMediaQuery("(max-width:1200px)");
    const isCompactHeader = useMediaQuery("(max-width:1450px)");
    const { user, hasPermission } = useAuth();
    const { t } = useTranslation();
    const { optionKeys } = useOptions();
    const enabledAdminModuleSet = React.useMemo(
        () => new Set(resolveEnabledAdminModules(optionKeys)),
        [optionKeys]
    );

    const [menuIndex, setMenuIndex] = React.useState(null);
    const [ccrOpen, setCcrOpen] = useState(false);
    const { selectedSiteData } = useSite();

    const handleMenuClick = (event, index, path) => {
        setAnchorEl(event.currentTarget);
        setMenuIndex(index); // Track which menu to open

    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuIndex(null); // Reset the menu index
    };

    const handleProfileClick = (event, path) => {
        setProfileAnchorEl(profileAnchorEl ? null : event.currentTarget); // Toggle the menu

    };

    const handleProfileClose = (path) => {
        setProfileAnchorEl(null);
        if (typeof path === "string") {
            navigate(path);
        }
    };

    const handleDocumentationNavigate = () => {
        if (!DOCUMENT_EXTERNAL_ENABLED || !DOCUMENT_EXTERNAL_URL) {
            navigate(ROUTES.DOCUMENT);
            return;
        }
        const username = selectedSiteData?.amf_method_username_ln;
        const password = selectedSiteData?.amf_method_password_ln;
        const loginUrl = buildExternalLoginUrl({
            templateUrl: DOCUMENT_EXTERNAL_URL,
            username,
            password,
        });
        if (!loginUrl) {
            navigate(ROUTES.DOCUMENT);
            return;
        }
        window.open(loginUrl, "_blank", "noopener,noreferrer");
    };

    const handleTrainingNavigate = async () => {
        try {
            await launchReach360();
        } catch (error) {
            if (error?.response?.status === 403) {
                navigate("/training");
                return;
            }
            toast.trigger({
                message: error?.response?.data?.message || error?.message || "Unable to open Reach 360.",
                severity: error?.code === "POPUP_BLOCKED" ? "warning" : "error",
            });
        }
    };

    const handleNavLinkNavigate = (link) => {
        if (link.path === ROUTES.DOCUMENT) {
            handleDocumentationNavigate();
            return;
        }
        if (link.path === "/training") {
            handleTrainingNavigate();
            return;
        }
        navigate(link.path);
    };

    const allNavlinks = [
        { label: t('LABELS.HOME'), path: ROUTES.HOME_ALIAS },
        {
            label: t('UI_TEXT.PARTS'),
            path: "/parts",
            subMenu: [
                { label: t('LABELS.SEARCH_PARTS'), path: "/search-results?partsonly=true" },
                { label: t('LABELS.MACHINESS'), path: "/machines" },
                { label: t('BUTTON.BUY_AGAIN'), path: "/order-history" },
                { label: t('LABELS.RSPL'), path: ROUTES.RSPL },
                { label: t('LABELS.MY_LISTS'), path: ROUTES.RECOMMENDATIONS },
                { label: t('LABELS.CUSTOMER_CARE_REQUESTS'), path: ROUTES.REQUESTS },
                { label: t('LABELS.SAVED_FOR_LATERS'), path: ROUTES.SAVED_FOR_LATER },
                // { label: t('LABELS.TRACK_ORDER'), path: ROUTES.TRACK_ORDER },
            ],
        },
        { label: t('LABELS.DOCUMENTATION'), path: ROUTES.DOCUMENT },
        {
            label: t('LABELS.TRAINING'),
            path: "/training",

        },
        {
            label: t('LABELS.SERVICE'), path: "/service", subMenu: [
                // { label: t('LABELS.DIGITAL_SOLUTIONS'), path: '/service/digital-solutions' },
                { label: t('LABELS.CASES_TICKETS'), path: '/service/cases' },
                { label: t('LABELS.WORK_ORDERS_REPORTS'), path: '/service/workorders' },
                // SOW item F — merged into the admin "Recommendation Approval" page (UAT 2026-08-17).
                // Reachable via Admin Panel > Recommendation Approval now, not a second nav entry here.
            ],
        },
        {
            label: t('LABELS.ANALYTIC'),
            path: null,
            subMenu: [
                { label: t("ITEMS_TEXT.ORDER_ANALYTICS", { defaultValue: "Order Analytics" }), path: ROUTES.ANALYTICS },
                { label: t("ITEMS_TEXT.USER_ANALYTICS", { defaultValue: "User Analytics" }), path: ROUTES.USER_ANALYTICS },
            ],
        },
    ];

    // Permission-driven visibility for Admin and Order History
    const isSuperAdmin = user?.user_id === 1;
    const isAdminRole = (user?.role?.role_name === 'Admin' || user?.role?.role_type === 'Admin') || user?.user_role === 'Admin';
    const canAccessAdminPage = isAdminRole || isSuperAdmin;
    const canSeeOrderAnalytics =
        canAccessOrderAnalytics(hasPermission, user) &&
        (isSuperAdmin || enabledAdminModuleSet.has("ITEMS_TEXT.ORDER_ANALYTICS"));
    const canSeeUserAnalytics =
        canAccessUserAnalytics(hasPermission, user) &&
        (isSuperAdmin || enabledAdminModuleSet.has("ITEMS_TEXT.USER_ANALYTICS"));
    const canSeeAnalytics = canSeeOrderAnalytics || canSeeUserAnalytics;
    const canSeeServices = !!hasPermission("service_management.view_cases") || !!hasPermission("service_management.view_workorders");
    const orderSchedulesEnabled = optionKeys?.enableOrderSchedules !== "false";
    const canSeeOrderHistory =
        !!hasPermission('order_management.view_order_history') ||
        (orderSchedulesEnabled && !!hasPermission('order_management.create_order_schedules'));
    const canSeeRequests = !!hasPermission("requests_management.allow_notify_customer_care");
    const canSeeSavedForLater = !!hasPermission("order_management.add_to_cart");
    const canSeeRecommendations = !!hasPermission("recommendations_management.add_parts")
        || !!hasPermission("recommendations_management.create_list")
        || !!hasPermission("recommendations_management.edit_list")
        || !!hasPermission("recommendations_management.delete_list");

    const filterSubMenu = (subMenu = []) =>
        subMenu.filter((subLink) => {
            switch (subLink.path) {
                case ROUTES.REQUESTS:
                    return canSeeRequests;
                case ROUTES.SAVED_FOR_LATER:
                    return canSeeSavedForLater;
                case ROUTES.RSPL:
                case ROUTES.RECOMMENDATIONS:
                    return canSeeRecommendations;
                case ROUTES.ORDER_HISTORY:
                    return canSeeOrderHistory;
                case ROUTES.ANALYTICS:
                    return canSeeOrderAnalytics;
                case ROUTES.USER_ANALYTICS:
                    return canSeeUserAnalytics;
                default:
                    return true;
            }
        });

    const Navlinks = allNavlinks
        .map((link) => (link.subMenu ? { ...link, subMenu: filterSubMenu(link.subMenu) } : link))
        .filter((link) => {
            // if (link.path === "/analytics") return canSeeAnalytics;
            if (link.subMenu?.some((subLink) => subLink.path === ROUTES.ANALYTICS || subLink.path === ROUTES.USER_ANALYTICS)) return canSeeAnalytics;
            if (link.path === "/service") return canSeeServices;
            if (!link.path && link.subMenu) return link.subMenu.length > 0;
            return true;
        });


    const toggleMobileMenu = () => setMobileOpen(!mobileOpen);

    const handleMobileNavClick = (link, event) => {
        event.stopPropagation(); // Prevent the click event from propagating

        if (link.subMenu) {
            // Toggle submenu visibility
            setActiveMobileSubmenu(activeMobileSubmenu === link.label ? null : link.label);
        } else {
            handleNavLinkNavigate(link);
            setActiveMobileSubmenu(null); // Hide any open submenu
        }
    };
    const settingsPermissionKeys = [
        'app_settings.manage_settings',
        'app_settings.manage_order_settings',
        'app_settings.manage_inventory_settings',
        'app_settings.manage_site_settings',
        'app_settings.manage_reminder_settings',
        'app_settings.manage_cart_retention',
    ];
    const canManageSettings = settingsPermissionKeys.some((perm) => hasPermission(perm));

    const canSeeAdmin = (() => {
        if (isSuperAdmin) return true;
        const checks = [
            ["ITEMS_TEXT.USERS", () => hasPermission('user_management.create_users') || hasPermission('user_management.edit_users') || hasPermission('user_management.delete_users')],
            ["ITEMS_TEXT.ROLES", () => hasPermission('role_management.create_roles') || hasPermission('role_management.edit_roles') || hasPermission('role_management.delete_roles')],
            ["ITEMS_TEXT.SITES", () => hasPermission('site_management.edit_sites')],
            ["ITEMS_TEXT.BANNERS", () => hasPermission('banner_management.view_banners') || hasPermission('banner_management.create_banners') || hasPermission('banner_management.edit_banners') || hasPermission('banner_management.delete_banners')],
            ["ITEMS_TEXT.DOCUMENTS", () => hasPermission('document_management.create_documents') || hasPermission('document_management.edit_documents') || hasPermission('document_management.delete_documents')],
            ["ITEMS_TEXT.MACHINE_PROVISION", () => hasPermission('machine_provision_management.provision_machines') || hasPermission('machine_provision_management.edit_provisions') || hasPermission('machine_provision_management.delete_provisions')],
            ["ITEMS_TEXT.PRICE_MARKUPS", () => hasPermission('app_settings.manage_price_markups')],
            ["ITEMS_TEXT.LINE_DISCOUNTS", () => hasPermission('line_discount_management.view_line_discounts') || hasPermission('line_discount_management.create_line_discounts') || hasPermission('line_discount_management.edit_line_discounts') || hasPermission('line_discount_management.delete_line_discounts')],
            ["ITEMS_TEXT.PROMOTIONS", () => hasPermission('promotion_management.view_promotions') || hasPermission('promotion_management.create_promotions') || hasPermission('promotion_management.edit_promotions') || hasPermission('promotion_management.delete_promotions')],
            ["ITEMS_TEXT.UPLOAD_MODELS", () => hasPermission('app_settings.allow_model_uploads')],
            ["ITEMS_TEXT.RECOMMENDATION_ACCEPTANCE", () => hasPermission('chip_governance.manage_chip_rules')],
			["ITEMS_TEXT.CHIP_REVIEW_APPROVAL", () => hasPermission('chip_governance.manage_chip_rules')],
            ["ITEMS_TEXT.SETTINGS", () => canManageSettings || hasPermission('app_settings.manage_default_values')],
            ["ITEMS_TEXT.POLICY_CENTER", () => hasPermission('app_settings.manage_policy_center')],
        ];
        return checks.some(([key, check]) => enabledAdminModuleSet.has(key) && check());
    })();



    // Build profile menu dynamically
    let profileMenuItems = [];


    profileMenuItems.push(
        { label: t("LABELS.PROFILE"), path: ROUTES.PROFILE, icon: <PersonIcon fontSize="small" />, divider: false },
        { label: t("LABELS.SUPPORT"), path: ROUTES.SUPPORT, icon: <EmailOutlinedIcon fontSize="small" />, divider: false }
    );


    // if (canSeeOrderHistory || canSeeOrderSchedules) {
    //     const menuItem = {
    //         label: canSeeOrderHistory
    //             ? t("LABELS.ORDER_HISTORYS")
    //             : "Order Schedules",
    //         path: ROUTES.ORDER_HISTORY,
    //         icon: <InventoryIcon fontSize="small" />,
    //         divider: true,
    //     };

    //     profileMenuItems.push(menuItem);
    // }


    if (canSeeAdmin && canAccessAdminPage) {
        profileMenuItems.push({
            label: t("MENU_ITEMS.ADMIN"),
            path: ROUTES.ADMIN,
            icon: <AdminPanelSettingsIcon fontSize="small" />,
            divider: true,
        });
    }


    profileMenuItems.push({
        label: t("LABELS.LOGOUT"),
        path: ROUTES.LOGOUT,
        icon: <LogoutIcon fontSize="small" />,
        divider: true, //   Divider before Logout
    });



    // Build label-to-route map dynamically from translated menu items
    const labelToRouteMap = profileMenuItems.reduce((map, item) => {
        map[item.label] = item.path;
        return map;
    }, {});

    // Navigation handler
    const handleProfileNavigate = (label) => {
        const route = labelToRouteMap[label];

        // Check if user has permission before navigating to admin
        if (route === ROUTES.ADMIN && !canAccessAdminPage) {
            setProfileAnchorEl(null);
            return;
        }

        // Check analytics access for viewers
        const isViewer = (user?.role?.role_name === 'Viewer' || user?.role?.role_type === 'Viewer') || user?.user_role === 'Viewer';
        if ((route === ROUTES.ANALYTICS || route === ROUTES.USER_ANALYTICS) && isViewer) {
            setProfileAnchorEl(null);
            return;
        }

        handleProfileClose();

        if (route === ROUTES.LOGOUT) {
            toast.trigger({ message: t('UI_TEXT.LOGGING_OUT'), severity: "warning" });
            setTimeout(() => {
                navigate(route);
            }, 2000);
        } else if (route) {
            navigate(route);
        } else {
            const fallbackPath = `/${label.toLowerCase().replace(/\s+/g, "-")}`;
            navigate(fallbackPath);
        }
    }

    return (
        <AppBar position="sticky" color="default" className="header-main" sx={{ p: 0 }}>
            <Toolbar
                sx={{
                    px: 1,
                    py: 0.75,
                    display: "flex",
                    alignItems: "center",
                    columnGap: { xs: 1, sm: 1.5, md: 2 },
                    rowGap: 1,
                    position: "relative",
                    flexWrap: "nowrap",
                }}
            >
                {/* LEFT */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: { xs: 1, sm: 1.5, md: 2 },
                        minWidth: 0,
                        flex: { xs: "0 1 auto", md: "0 1 auto" },
                        order: { xs: 1, md: 1 },
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", width: { xs: "112px", sm: "124px", md: isCompactHeader ? "132px" : "150px" } }}>
                        <CompanyLogo
                            alt="logo"
                            onClick={() => navigate(ROUTES.HOME_ALIAS)}
                            style={{ cursor: "pointer", maxWidth: isCompactHeader ? "122px" : "140px" }}
                            skeletonWidth={140}
                            skeletonHeight={40}
                        />
                    </Box>

                    <SiteSelection />
                </Box>

                <Box
                    sx={{
                        flex: "1 1 auto",
                        minWidth: 0,
                        display: "flex",
                        justifyContent: "center",
                        pointerEvents: "auto",
                        order: { xs: 2, md: 2 },
                        px: isCompactHeader ? 0.5 : 0,
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            minWidth: 0,
                            maxWidth: {
                                xs: "100%",
                                sm: "100%",
                                md: isCompactHeader ? "min(100%, 680px)" : "clamp(320px, 40vw, 700px)",
                                lg: "clamp(360px, 35vw, 760px)",
                            },
                        }}
                    >
                        <GlobalSearch width="100%" />
                    </Box>
                </Box>

                {/* RIGHT */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: { xs: 0.75, sm: 1, md: 1.25, lg: 2 },
                        minWidth: "fit-content",
                        flex: "0 0 auto",
                        justifyContent: { xs: "flex-end", md: "flex-start" },
                        order: { xs: 3, md: 3 },
                        flexWrap: "nowrap",
                    }}
                >
                    <CartIcon />
                    <NotificationBell />
                    {setChatOpen && showChatbot && (
                        <Tooltip title={chatOpen ? "Close CHIP" : "Ask CHIP"} arrow placement="bottom">
                            <Button
                                variant="roundedGreyBgSquareContained"
                                color="primary"
                                onClick={() => setChatOpen((prev) => !prev)}
                                sx={chatOpen ? { bgcolor: (theme) => `${theme.palette.primary.main}1A` } : {}}
                            >
                                <img src="/chip-icon.png" alt="CHIP" style={{ width: 22, height: 22, objectFit: "contain", borderRadius: "50%" }} />
                            </Button>
                        </Tooltip>
                    )}
                    <Tooltip title={t("LABELS.CONTACT_CCR")} arrow placement="bottom">
                        <Button variant="roundedGreyBgSquareContained" color="primary" onClick={() => setCcrOpen(true)}>
                            <EmailOutlinedIcon />
                        </Button>
                    </Tooltip>

                    {/* MUI Links (ALL CAPS): Support, Profile */}
                    <Box sx={{ display: "flex", alignItems: "center", columnGap: { xs: 0.75, sm: 1, md: 1.25, lg: 2 } }}>
                        <Tooltip title={t("UI_TEXT.TOOLTIP_NAVIGATE_TO_SUPPORT")} placement="bottom" arrow>
                            <Link
                                underline="none"
                                onClick={() => { handleProfileNavigate(t("LABELS.SUPPORT")) }} // adjust
                                color="#000"
                                sx={{ cursor: 'pointer' }}
                            >
                                <Typography variant="body1" sx={{ fontSize: '14px' }} fontWeight={"bold"} color="#000">
                                    SUPPORT
                                </Typography>
                            </Link>
                        </Tooltip>

                        <Tooltip title={t("UI_TEXT.TOOLTIP_NAVIGATE_TO_PROFILE", { defaultValue: "View / Update your user profile" })} placement="bottom" arrow>
                            <Link
                                underline="none"
                                onClick={() => { handleProfileNavigate(t("LABELS.PROFILE")) }} // adjust
                                color="#000"
                                sx={{ cursor: 'pointer' }}
                            >
                                <Typography variant="body1" sx={{ fontSize: '14px' }} fontWeight={"bold"} color="#000">
                                    PROFILE
                                </Typography>
                            </Link>
                        </Tooltip>
                    </Box>

                    <IconButton color="inherit" onClick={handleProfileClick} sx={{ p: isCompactHeader ? 0.35 : 1 }}>
                        <Avatar {...stringAvatar(`${user?.user_first_name} ${user?.user_last_name}`, 40, 40, 16)} />
                    </IconButton>

                    <Menu
                        anchorEl={profileAnchorEl}
                        open={Boolean(profileAnchorEl)}
                        onClose={handleProfileClose}
                        sx={{ zIndex: (theme) => theme.zIndex.appBar + 2 }}
                        disableScrollLock
                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                        slotProps={{
                            root: { className: "header-menu-popover" },
                            paper: {
                                elevation: 0,
                                sx: {
                                    overflow: "visible",
                                    minWidth: "200px",
                                    borderRadius: "10px",
                                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                                    mt: 1.25,
                                    "& .MuiAvatar-root": { width: 32, height: 32, mr: 1 },
                                    "&::before": {
                                        content: '""',
                                        display: "block",
                                        position: "absolute",
                                        top: 0,
                                        right: 14,
                                        width: 10,
                                        height: 10,
                                        bgcolor: "background.paper",
                                        transform: "translateY(-50%) rotate(45deg)",
                                        // zIndex: 0,
                                    },
                                },
                            },
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "flex-start", px: 1.75, py: 0.5 }}>
                            <UserName name={user?.user_display_name} email={user?.user_email || "N/A"} />
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        {profileMenuItems.map((item, index) => {
                            const isLogout = item?.label === t("LABELS.LOGOUT");
                            return (
                                <React.Fragment key={index}>
                                    {item.divider && <Divider sx={{ my: 1 }} />
                                    }
                                    <MenuItem
                                        onClick={() => handleProfileNavigate(item.label)}
                                        sx={
                                            isLogout
                                                ? {
                                                    color: "error.main",
                                                    "&:hover": { bgcolor: (theme) => alpha(theme.palette.error.light, 0.1) },
                                                }
                                                : {}
                                        }
                                    >
                                        <ListItemIcon sx={isLogout ? { color: "error.main" } : {}}>{item.icon}</ListItemIcon>
                                        <Typography variant="body1" fontSize="13px" fontWeight="500">
                                            {item.label}
                                        </Typography>
                                    </MenuItem>
                                </React.Fragment>
                            );
                        })}
                    </Menu>
                </Box>
            </Toolbar>

            <Box sx={{ height: '50px', background: '#222', width: '100%', p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    {Navlinks.map((link, index) =>
                        link.subMenu ? (
                            <div key={index}>
                                <Button color="inherit" sx={{ px: 2, pr: 1, color: '#fff', textTransform: 'uppercase', fontSize: '14px' }} onClick={(event) => handleMenuClick(event, index)}>
                                    {link.label.toUpperCase()} <ArrowDropDownIcon sx={{ ml: 0.75 }} />
                                </Button>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl) && menuIndex === index}
                                    onClose={handleMenuClose}
                                    sx={{ zIndex: (theme) => theme.zIndex.appBar + 2 }}
                                    disableScrollLock={true}
                                    slotProps={{
                                        root: { className: "header-menu-popover" },
                                        paper: {
                                            elevation: 0,
                                            sx: {
                                                overflow: "visible",
                                                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                                                mt: 1.5,
                                                "& .MuiAvatar-root": {
                                                    width: 32,
                                                    height: 32,
                                                    ml: -0.5,
                                                    mr: 1,
                                                },
                                                "&::before": {
                                                    content: '""',
                                                    display: "block",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 14,
                                                    width: 10,
                                                    height: 10,
                                                    bgcolor: "background.paper",
                                                    transform: "translateY(-50%) rotate(45deg)",
                                                    zIndex: 0,
                                                },
                                            },
                                        },
                                    }}
                                    transformOrigin={{ horizontal: "left", vertical: "top" }}
                                    anchorOrigin={{ horizontal: "left", vertical: "bottom" }}>
                                    {link.subMenu.map((subLink, subIndex) => (
                                        <MenuItem
                                            key={subIndex}
                                            onClick={() => {
                                                navigate(subLink.path);
                                                handleMenuClose();
                                            }}>
                                            <b>{subLink.label}</b>
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </div>
                        ) : (
                            <Button
                                key={index}
                                sx={{
                                    px: 2,
                                    color: "#fff",
                                    textTransform: "uppercase",
                                    fontSize: "14px"
                                }}
                                color="inherit"
                                onClick={() => {
                                    handleNavLinkNavigate(link);
                                }}
                            >
                                {link.label.toUpperCase()}
                            </Button>

                        )
                    )}
                </Box>
                <Box>
                    <WelcomeBox user={user} />
                </Box>
            </Box>
            {/* Contact CCR modal opened from header */}
            <ContactCCRForm open={ccrOpen} setOpen={setCcrOpen} />
        </AppBar>
    );
};

export default Header;
